import React, {useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import {AspectRatio, Flex, Img, List, ListItem, useMediaQuery} from '@chakra-ui/react'
import {remCalc} from '../../../theme/_utils'
import ChevronUp from '../../icons/arrows/ChevronUp'
import ChevronDown from '../../icons/arrows/ChevronDown'
import ProductThumbnail from '../../product-thumbnail'
import Icon360 from '../../icons/others/Icon360'
import PlayIcon from '../../icons/action/PlayIcon'
import ImageGalleryDivider from '../../dividers/image-gallery-divider'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {handleChevronStyles} from './utils'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {parseAndSanitizeJSON} from '../../../utils/utils'

const GallerySlider = ({
    loadingStrategy,
    videoUrl,
    styles,
    selectedIndex,
    setSelectedIndex,
    scrollPosition,
    setScrollPosition,
    thumbnailImages,
    swiperRef,
    scrollRef,
    stopPropagation,
    setIsModalGallerySwiper = noop,
    isModalOpen,
    modalInitValue,
}) => {
    const imageRef = useRef(null)
    const imageRefScroll = imageRef && imageRef.current && imageRef.current.offsetHeight

    useEffect(() => {
        if (isModalOpen && typeof modalInitValue === 'number') {
            handleSelection(modalInitValue)
        }
    }, [isModalOpen])

    const [isDesktop] = useMediaQuery(`(min-width: ${remCalc(1024)})`)
    const video = parseAndSanitizeJSON(videoUrl)?.[0]

    //* Placeholder variable for video & 360 icon
    const is360Enabled = false

    const handleVerticalScroll = (amount) => {
        if (!scrollRef.current) return
        scrollRef.current.scrollTop += amount
        setScrollPosition((scrollRef.current.scrollTop += amount))
    }

    let middlePosition, top, bottom, updatedScrollPosition

    if (isDesktop) {
        middlePosition = scrollRef?.current?.offsetHeight / 2
        top = scrollPosition <= 0
        bottom =
            scrollPosition >= scrollRef?.current?.scrollHeight - scrollRef?.current?.clientHeight
        updatedScrollPosition = top ? 'top' : bottom ? 'bottom' : 'between'
    } else {
        middlePosition = scrollRef?.current?.offsetWidth / 2
        top = scrollPosition <= 0
        bottom = scrollPosition >= scrollRef?.current?.scrollWidth - scrollRef?.current?.clientWidth
        updatedScrollPosition = top ? 'top' : bottom ? 'bottom' : 'between'
    }

    const shouldShowBlur =
        !(
            selectedIndex === thumbnailImages.length - 2 ||
            selectedIndex === thumbnailImages.length - 1
        ) && !isDesktop

    const m = isModalOpen ? (videoUrl ? remCalc(-75) : remCalc(-14)) : remCalc(-32)
    const marginSliderContainer = {
        '@media (max-width: 1023px)': {
            margin: '0 auto',
        },
        '@media (min-width: 1024px)': {
            margin: `${m} 0 0 0`,
        },
    }

    const sliderWidth = videoUrl ? `calc(100% - ${remCalc(84)})` : '100%'

    const handleSelection = (index) => {
        const gapWidth = isDesktop ? 8 : 16
        const newPos = index * gapWidth + (index + 0.5) * imageRefScroll - middlePosition
        // scroll horizzontally to newPosition
        if (isDesktop) {
            scrollRef.current.scrollTop = newPos
        } else {
            scrollRef.current.scrollLeft = newPos
        }

        setIsModalGallerySwiper(true)
        setScrollPosition(newPos)
        setSelectedIndex(index)
        stopPropagation()
        swiperRef && swiperRef.current && swiperRef.current.slideTo(index)
    }

    return (
        <Flex {...styles.sliderContainer} sx={marginSliderContainer}>
            <Flex
                {...styles.thumbnailImagesUpDownArrows}
                onClick={() => handleVerticalScroll(-(imageRefScroll / 2 + 8))}
                {...handleChevronStyles(
                    'vertical',
                    thumbnailImages.length,
                    'top',
                    updatedScrollPosition,
                )}
                data-testid="thumbnail-up-arrow"
            >
                <ChevronUp />
            </Flex>
            <Flex position={'relative'} width={sliderWidth}>
                <Flex
                    {...styles.sliderBlurredContainer}
                    sx={
                        shouldShowBlur
                            ? {
                                  _before: {
                                      content: '""',
                                      position: 'absolute',
                                      top: 0,
                                      bottom: 0,
                                      right: 0,
                                      bg: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%)',
                                      width: '29%',
                                      pointerEvents: 'none',
                                      zIndex: 10,
                                  },
                              }
                            : {}
                    }
                >
                    <List
                        {...styles.thumbnailImageList}
                        sx={{
                            '::-webkit-scrollbar': {display: 'none'},
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none',
                        }}
                        ref={scrollRef}
                    >
                        {thumbnailImages.map((image, index) => {
                            const selected = index === selectedIndex
                            return (
                                <ListItem key={index} height="fit-content">
                                    <ProductThumbnail
                                        image={image}
                                        imageRef={imageRef}
                                        handleSelection={(index) => handleSelection(index)}
                                        index={index}
                                        selected={selected}
                                        loadingStrategy={loadingStrategy}
                                    />
                                </ListItem>
                            )
                        })}
                    </List>
                </Flex>
            </Flex>
            <Flex
                {...styles.thumbnailImagesUpDownArrows}
                {...handleChevronStyles(
                    'vertical',
                    thumbnailImages.length,
                    'bottom',
                    updatedScrollPosition,
                )}
                onClick={() => handleVerticalScroll(imageRefScroll / 2 + 8)}
                data-testid="thumbnail-down-arrow"
            >
                <ChevronDown />
            </Flex>
            {videoUrl && (
                <>
                    <Flex {...styles.dividerContainer} {...styles.mobileMargin}>
                        <ImageGalleryDivider />
                    </Flex>
                    <Flex {...styles.mobileMargin}>
                        <AspectRatio
                            ratio={1}
                            {...styles.thumbnailVideoSkeleton}
                            cursor="pointer"
                            onClick={() => {
                                setIsModalGallerySwiper(false)
                                setSelectedIndex(video?.code)
                                stopPropagation()
                                swiperRef &&
                                    swiperRef.current &&
                                    swiperRef.current.slideTo(video?.code)
                            }}
                        >
                            <>
                                <Img
                                    alt={video?.title}
                                    loading="lazy"
                                    src={getAssetUrl('static/img/thumbnail.webp')}
                                />
                                <Flex width="100%" height="100%">
                                    <PlayIcon
                                        color="background.dark"
                                        fill="background.default"
                                        width="50%"
                                        height="50%"
                                        data-testid="play-button-thumbnail"
                                    />
                                </Flex>
                            </>
                        </AspectRatio>
                    </Flex>
                </>
            )}
            {is360Enabled && (
                <Flex {...styles.icon360}>
                    <Icon360 width={remCalc(40)} />
                </Flex>
            )}
        </Flex>
    )
}

GallerySlider.propTypes = {
    scrollRef: PropTypes.object,
    swiperRef: PropTypes.object,
    loadingStrategy: PropTypes.string,
    videoUrl: PropTypes.string,
    styles: PropTypes.object,
    scrollPosition: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    setScrollPosition: PropTypes.func,
    selectedIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    setSelectedIndex: PropTypes.func,
    thumbnailImages: PropTypes.array,
    stopPropagation: PropTypes.func,
    openModal: PropTypes.func,
    setIsModalGallerySwiper: PropTypes.func,
    isModalOpen: PropTypes.bool,
    modalInitValue: PropTypes.number,
}

export default GallerySlider
